import { useEffect, useMemo, useState, useCallback } from 'react';
import { AnalyticsPayload, ApiResponse, ChangelogEntry, SortDir, SortKey } from './types';
import { buildRows, filterRows, findRowByKey, mergeApiResponses, mergeChangelogs, sortRows } from './lib/models';
import { countryFromSelector, countryOf, ResidencyScope } from './lib/regions';
import { FilterBar } from './components/FilterBar';
import { ModelTable } from './components/ModelTable';
import { ModelDrawer } from './components/ModelDrawer';
import { WhatsNew } from './components/WhatsNew';
import { ReleaseNotes } from './components/ReleaseNotes';
import { ThemeToggle } from './components/ThemeToggle';
import { LocationView } from './views/LocationView';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

type View = 'location' | 'model' | 'notes';

const SORT_KEYS: readonly string[] = ['providerName', 'modelName', 'modelId', 'isCri', 'regions'];
const SCOPES: readonly string[] = ['region', 'country', 'geo'];

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });
}

function formatDateOnly(isoDate: string) {
  return new Date(isoDate).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function readParams() {
  const p = new URLSearchParams(window.location.search);
  const sort = p.get('sort') ?? '';
  const scope = p.get('scope') ?? '';
  const view = p.get('view') ?? '';
  const region = p.get('region') ?? '';
  return {
    // Legacy links: ?region= (old filter) still lands on the location answer
    view:           (view === 'model' || view === 'location' || view === 'notes' ? view : 'location') as View,
    region,
    scope:          (SCOPES.includes(scope) ? scope : 'region') as ResidencyScope,
    search:         p.get('q')        ?? '',
    provider:       p.get('provider') ?? '',
    activeOnly:     p.get('active')   === '1',
    inputModality:  p.get('input')    ?? '',
    outputModality: p.get('output')   ?? '',
    sortKey:        (SORT_KEYS.includes(sort) ? sort : 'providerName') as SortKey,
    sortDir:        (p.get('dir') === 'desc' ? 'desc' : 'asc') as SortDir,
    model:          p.get('model')    ?? '',
  };
}

export default function App() {
  const [data, setData]             = useState<ApiResponse | null>(null);
  const [changelog, setChangelog]   = useState<ChangelogEntry[]>([]);
  const [analytics, setAnalytics]   = useState<AnalyticsPayload | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const init = readParams();
  const [view, setView]                   = useState<View>(init.view);
  const [region, setRegion]               = useState(init.region);
  const [scope, setScope]                 = useState<ResidencyScope>(init.scope);
  const [search, setSearch]               = useState(init.search);
  const [provider, setProvider]           = useState(init.provider);
  const [activeOnly, setActiveOnly]       = useState(init.activeOnly);
  const [inputModality, setInputModality] = useState(init.inputModality);
  const [outputModality, setOutputModality] = useState(init.outputModality);
  const [sortKey, setSortKey]             = useState<SortKey>(init.sortKey);
  const [sortDir, setSortDir]             = useState<SortDir>(init.sortDir);
  // rowKey ("cloud|modelId"), or a bare modelId for links predating clouds
  const [openKey, setOpenKey]             = useState(init.model);

  // Best-effort fetch for a secondary cloud's data — a 404 (no refresh yet)
  // or non-JSON response just means "nothing from this cloud yet," not an
  // error, unlike the primary AWS fetch below.
  const fetchOptionalJson = useCallback(async (path: string): Promise<ApiResponse | null> => {
    try {
      const res = await fetch(`${API_BASE}${path}`);
      const contentType = res.headers.get('content-type') ?? '';
      if (!res.ok || !contentType.includes('json')) return null;
      return await res.json() as ApiResponse;
    } catch {
      return null;
    }
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/data/models.json`);
      if (!res.ok) throw new Error(`Data fetch failed (HTTP ${res.status})`);
      // A missing object falls through CloudFront's SPA error page and returns
      // index.html with a 200, so guard on content type rather than status alone.
      const contentType = res.headers.get('content-type') ?? '';
      if (!contentType.includes('json')) {
        throw new Error('Model data has not been generated yet. The first daily refresh may still be pending — try again later.');
      }
      const aws = await res.json() as ApiResponse;
      const azure = await fetchOptionalJson('/data/models-azure.json');
      setData(mergeApiResponses(aws, azure));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    }
  }, [fetchOptionalJson]);

  const load = useCallback(() => {
    setLoading(true);
    fetchModels().finally(() => setLoading(false));
  }, [fetchModels]);

  useEffect(() => { load(); }, [load]);

  // Changelog is optional — older deployments won't have it, so fail silently.
  // Each cloud writes its own changelog file independently, same as models.
  const fetchChangelogEntries = useCallback(async (path: string): Promise<ChangelogEntry[]> => {
    try {
      const res = await fetch(`${API_BASE}${path}`);
      const ct = res.headers.get('content-type') ?? '';
      if (!res.ok || !ct.includes('json')) return [];
      const cl = await res.json();
      return Array.isArray(cl?.entries) ? cl.entries as ChangelogEntry[] : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    Promise.all([
      fetchChangelogEntries('/data/changelog.json'),
      fetchChangelogEntries('/data/changelog-azure.json'),
    ]).then(([aws, azure]) => setChangelog(mergeChangelogs(aws, azure)));
  }, [fetchChangelogEntries]);

  // Visitor counts are a nice-to-have, not core data — a missing or
  // not-yet-generated file (first day after deploy) fails silently.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/data/analytics.json`);
        const ct = res.headers.get('content-type') ?? '';
        if (!res.ok || !ct.includes('json')) return;
        setAnalytics(await res.json() as AnalyticsPayload);
      } catch {
        // ignore — footer simply omits the visitor stat
      }
    })();
  }, []);

  const allRows = useMemo(() => (data ? buildRows(data) : []), [data]);

  const providers = useMemo(
    () => [...new Set(allRows.map(r => r.providerName))].sort(),
    [allRows],
  );

  const inputModalities = useMemo(
    () => [...new Set(allRows.flatMap(r => r.inputModalities))].sort(),
    [allRows],
  );

  const outputModalities = useMemo(
    () => [...new Set(allRows.flatMap(r => r.outputModalities))].sort(),
    [allRows],
  );

  const allRegions = useMemo(
    () => [...new Set(allRows.flatMap(r => r.regions.map(re => re.region)))].sort(),
    [allRows],
  );

  const regionLabels = useMemo(() => data?.regionLabels ?? {}, [data]);

  // Drop a stale region (or country selector) from an old link once we
  // know the real region list
  useEffect(() => {
    if (!data || !region) return;
    const country = countryFromSelector(region);
    const stillValid = country
      ? allRegions.some(r => countryOf(r) === country)
      : allRegions.includes(region);
    if (!stillValid) setRegion('');
  }, [data, region, allRegions]);

  const filtered = useMemo(
    () => filterRows(allRows, { search, provider, activeOnly, inputModality, outputModality }),
    [allRows, search, provider, activeOnly, inputModality, outputModality],
  );

  const sorted = useMemo(() => sortRows(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

  const openRow = useMemo(
    () => findRowByKey(allRows, openKey) ?? null,
    [allRows, openKey],
  );

  useEffect(() => {
    const p = new URLSearchParams();
    if (view !== 'location') p.set('view', view);
    if (region)        p.set('region',   region);
    if (scope !== 'region') p.set('scope', scope);
    if (search)        p.set('q',        search);
    if (provider)      p.set('provider', provider);
    if (activeOnly)    p.set('active',   '1');
    if (inputModality)  p.set('input',   inputModality);
    if (outputModality) p.set('output',  outputModality);
    if (sortKey !== 'providerName' || sortDir !== 'asc') {
      p.set('sort', sortKey);
      p.set('dir',  sortDir);
    }
    if (openKey)       p.set('model',    openKey);
    const qs = p.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [view, region, scope, search, provider, activeOnly, inputModality, outputModality, sortKey, sortDir, openKey]);

  const hasActiveFilters = !!(search || provider || activeOnly || inputModality || outputModality);

  function clearFilters() {
    setSearch(''); setProvider(''); setActiveOnly(false);
    setInputModality(''); setOutputModality('');
  }

  function handleSort(key: SortKey) {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function openModel(key: string) {
    setOpenKey(key);
  }

  const profileCount = data?.inferenceProfiles?.length ?? 0;

  const staleDays = data
    ? Math.floor((Date.now() - new Date(data.updatedAt).getTime()) / 86_400_000)
    : 0;

  const latestVisitorDay = analytics && analytics.days.length > 0
    ? analytics.days[analytics.days.length - 1]
    : null;
  const last7DaysVisitors = analytics
    ? analytics.days.slice(-7).reduce((sum, d) => sum + d.visitors, 0)
    : null;

  return (
    <div className="app">
      <header>
        <h1>LLM Availability</h1>
        <span className="subtitle">AWS &amp; Azure · availability by region · data residency</span>
        {data && (
          <span className="updated">Updated {formatDate(data.updatedAt)}</span>
        )}
        <ThemeToggle />
      </header>

      <div className="disclaimer">
        Data is offered on a best-endeavours basis and may not reflect real-time AWS or Azure availability.
        Always verify directly with the provider before relying on this information for production workloads.
      </div>

      {staleDays >= 2 && (
        <div className="stale-banner">
          ⚠ This data was last refreshed {staleDays} days ago and may be out of date.
        </div>
      )}

      {loading ? (
        <div className="state-center">
          <div className="spinner" />
          <span>Loading model data…</span>
        </div>
      ) : error ? (
        <div className="main">
          <div className="error-box">
            <strong>Failed to load data</strong>
            <p>{error}</p>
            <button className="retry-btn" onClick={load}>Retry</button>
          </div>
        </div>
      ) : (
        <>
          {view !== 'notes' && (
            <div className="stats-bar">
              <div className="stat">
                <span className="stat-value">{allRows.length}</span>
                <span className="stat-label">Unique Models</span>
              </div>
              <div className="stat">
                <span className="stat-value">{allRegions.length}</span>
                <span className="stat-label">Regions</span>
              </div>
              <div className="stat">
                <span className="stat-value">{profileCount}</span>
                <span className="stat-label">Routing Profiles</span>
              </div>
              <div className="stat">
                <span className="stat-value">{data?.modelCount ?? 0}</span>
                <span className="stat-label">Total Listings</span>
              </div>
            </div>
          )}

          <main className="main">
            {view === 'notes' ? (
              <>
                <button className="back-link" onClick={() => setView('location')}>← Back to app</button>
                <ReleaseNotes />
              </>
            ) : (
              <>
                <WhatsNew entries={changelog} />

                <div className="view-switch" role="tablist" aria-label="View">
                  <button
                    role="tab"
                    aria-selected={view === 'location'}
                    className={`view-tab${view === 'location' ? ' active' : ''}`}
                    onClick={() => setView('location')}
                  >
                    By location
                  </button>
                  <button
                    role="tab"
                    aria-selected={view === 'model'}
                    className={`view-tab${view === 'model' ? ' active' : ''}`}
                    onClick={() => setView('model')}
                  >
                    By model
                  </button>
                </div>

                <FilterBar
                  search={search} onSearch={setSearch}
                  provider={provider} onProvider={setProvider} providers={providers}
                  activeOnly={activeOnly} onActiveOnly={setActiveOnly}
                  showModalities
                  inputModality={inputModality} onInputModality={setInputModality} inputModalities={inputModalities}
                  outputModality={outputModality} onOutputModality={setOutputModality} outputModalities={outputModalities}
                  hasActiveFilters={hasActiveFilters} onClear={clearFilters}
                  resultCount={filtered.length} totalCount={allRows.length}
                  apiData={data}
                />

                {view === 'location' ? (
                  <LocationView
                    rows={filtered}
                    allRegions={allRegions}
                    regionLabels={regionLabels}
                    region={region} onRegion={setRegion}
                    scope={scope} onScope={setScope}
                    onOpenModel={openModel}
                  />
                ) : (
                  <ModelTable
                    rows={sorted}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                    onOpenModel={openModel}
                    regionLabels={regionLabels}
                  />
                )}
              </>
            )}
          </main>
        </>
      )}

      {openRow && (
        <ModelDrawer
          row={openRow}
          regionLabels={regionLabels}
          onClose={() => setOpenKey('')}
        />
      )}

      <footer>
        <div className="footer-col">
          <span className="footer-col-label">About</span>
          <p>Data pulled from the AWS Bedrock and Azure AI Foundry APIs and refreshed daily.</p>
        </div>

        {latestVisitorDay && (
          <div className="footer-col">
            <span className="footer-col-label">Traffic</span>
            <p>
              {latestVisitorDay.visitors.toLocaleString()} visitor{latestVisitorDay.visitors === 1 ? '' : 's'}
              {' '}on {formatDateOnly(latestVisitorDay.date)}
            </p>
            {last7DaysVisitors !== null && (
              <p>{last7DaysVisitors.toLocaleString()} in the last 7 days</p>
            )}
          </div>
        )}

        <div className="footer-col footer-col-links">
          <span className="footer-col-label">Links</span>
          <a href="https://github.com/hasack/LLM_Availability" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
          <a href="?view=notes" onClick={e => { e.preventDefault(); setView('notes'); }}>Release notes</a>
          <a href="/data/feed.xml">Subscribe: RSS feed</a>
        </div>
      </footer>
    </div>
  );
}
